import { Component, signal, computed, OnInit, input, output } from '@angular/core';
import { ColorConverter, ColorInfo } from '../../../../core/util/color-converter';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-color-selector',
  standalone: true,
  templateUrl: './color-selector.component.html',
  styleUrls: ['./color-selector.component.scss'],
  imports: [NgClass],
})
export class ColorSelectorComponent implements OnInit {
  // Inputs and outputs
  material = input<'Powder Coated' | 'Anodized'>('Powder Coated');
  selectedColor = output<ColorInfo>();
  closeModal = output<void>();

  // Computed properties and signals
  palette = computed(() => (this.material() === 'Powder Coated' ? 'RAL' : 'Anodized'));
  searchTerm = signal('');
  recentlyUsedRAL = signal<string[]>([]);

  // All colors and storage key
  allColors: ColorInfo[] = [];
  key: string = 'recentRALcolors';

  // Popular RAL codes and computed lists
  private readonly popularRALCodes = [
    '9010', '9001', '7016', '7035', '7015', '7032', '7031', '7012',
  ];

  recentlyUsedColors = computed(() =>
      this.recentlyUsedRAL()
          .map((ral) => ColorConverter.convert(ral))
          .filter((info): info is ColorInfo => !!info)
  );

  popularColors = computed(() => this.filterColors(true));
  otherColors = computed(() => this.filterColors(false));

  // Lifecycle hooks
  ngOnInit() {
    this.updateColors();
    this.loadRecentlyUsedFromStorage();
  }

  // Search functionality
  onSearch(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    this.searchTerm.set(inputElement.value);
  }

  // Color selection
  onSelectColor(ralCode: string) {
    const updatedList = [ralCode, ...this.recentlyUsedRAL().filter((r) => r !== ralCode)].slice(0, 10);
    this.recentlyUsedRAL.set(updatedList);
    sessionStorage.setItem(this.key, JSON.stringify(updatedList));
    this.selectedColor.emit(ColorConverter.convert(ralCode));
  }

  // Overlay click
  onOverlayClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.closeModal.emit();
    }
  }

  // Utility: Get text color based on luminance
  getTextColor(hex: string): string {
    return ColorConverter.getLuminance(hex) > 0.88 ? 'text-slate-700' : 'text-slate-50';
  }

  // Private methods

  /** Updates the list of all colors and the storage key based on the palette. */
  private updateColors() {
    this.allColors = ColorConverter.getAllColors(this.palette());
    this.key = this.palette() === 'RAL' ? 'recentRALcolors' : 'recentAnodizedColors';
  }

  /** Filters colors based on whether they are popular and match the search term. */
  private filterColors(popular: boolean): ColorInfo[] {
    const term = this.searchTerm().trim().toLowerCase();
    const filterPopular = (c: ColorInfo) =>
        popular ? this.popularRALCodes.includes(c.RAL) : !this.popularRALCodes.includes(c.RAL);

    return this.allColors.filter(
        (c) =>
            filterPopular(c) &&
            (!term || c.RAL.toLowerCase().includes(term) || c.name.toLowerCase().includes(term))
    );
  }

  /** Loads recently used colors from session storage. */
  private loadRecentlyUsedFromStorage() {
    try {
      const stored = sessionStorage.getItem(this.key);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          this.recentlyUsedRAL.set(parsed);
        }
      }
    } catch (e) {
      console.warn('Failed to parse recent colors from session storage:', e);
    }
  }
}
